/**
 * Field-level and section-level edit policy for HCP request drafts.
 * Merge server hints from `populated.meta.fieldEditPolicy` when present.
 *
 * @typedef {'editable' | 'readOnly'} FieldMode
 */

/** @type {Record<string, FieldMode>} */
const DEFAULT_FIELDS = {
  'applicantInfo.email': 'readOnly',
  'applicantInfo.idNumber': 'readOnly',
  'applicantInfo.idImage': 'readOnly',
  'applicantInfo.countryCode': 'editable',
  'applicantInfo.phoneNumber': 'editable',
  'qualifications.education': 'readOnly',
};

/** @type {Record<string, FieldMode>} */
const DEFAULT_SECTIONS = {
  applicantInfo: 'editable',
  licenseInfo: 'readOnly',
  qualifications: 'editable',
};

/**
 * @param {object} [serverPolicy] — `populated.meta.fieldEditPolicy` shape:
 *   `{ sections?: Record<string, FieldMode>, fields?: Record<string, FieldMode> }`
 */
export function mergeRequestDraftFieldPolicy(serverPolicy) {
  const s = serverPolicy?.sections || {};
  const f = serverPolicy?.fields || {};
  return {
    sections: { ...DEFAULT_SECTIONS, ...s },
    fields: { ...DEFAULT_FIELDS, ...f },
  };
}

/**
 * @param {string} path — e.g. `applicantInfo.firstName`, `licenseInfo.licenseNumber`
 * @param {{ sections: Record<string, FieldMode>, fields: Record<string, FieldMode> }} policy
 * @returns {FieldMode}
 */
export function resolveFieldMode(path, policy) {
  const direct = policy.fields[path];
  if (direct === 'readOnly' || direct === 'editable') {
    return direct;
  }

  if (path.startsWith('qualifications.education')) {
    const edu = policy.fields['qualifications.education'];
    if (edu === 'readOnly' || edu === 'editable') {
      return edu;
    }
  }

  const section = path.split('.')[0];
  const secMode = policy.sections[section];
  if (secMode === 'readOnly' || secMode === 'editable') {
    return secMode;
  }
  return 'editable';
}
